import { useEffect, useState } from "react";
import { endpoints, fmtUSD } from "@/lib/api";
import { RefreshCcw, Zap, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Renewals() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    endpoints.expiringSubs().then((r) => setSubs(r.subscriptions));
  }, []);

  const dispatchAutopilot = async (sub) => {
    setLoading(sub.subscription_id);
    const intent = `Renewal autopilot: The direct AWS Marketplace subscription ${sub.subscription_id} for ${sub.buyer_account} (AWS account ${sub.buyer_aws_account_id}, product ${sub.product_name}, current ARR ${sub.current_arr_usd}) expires on ${sub.expires_at}. Please find expiring subscriptions, look up the "Renewal (ABO)" rate card, get MCP hints for the buyer AWS account, draft an Agreement-Based Offer authorization through ${sub.recommended_reseller}, and request human approval before publishing.`;
    try {
      const r = await endpoints.startRun(intent, { is_renewal: true });
      toast.success("Renewal autopilot dispatched", { description: r.id });
      nav("/agent");
    } catch (e) {
      toast.error("Failed to dispatch");
    } finally {
      setLoading(null);
    }
  };

  const urgencyColor = (days) => {
    if (days <= 30) return "text-rose-600 border-rose-500/40 bg-rose-500/5";
    if (days <= 60) return "text-orange-600 border-orange-500/40 bg-orange-50";
    return "text-amber-700 border-amber-500/40 bg-amber-500/5";
  };

  return (
    <div className="max-w-[1200px] px-12 py-14" data-testid="page-renewals">
      <div className="tx-overline mb-2">Module M7 · autopilot</div>
      <h1 className="font-heading font-black text-4xl mb-2">Renewal Autopilot</h1>
      <p className="text-zinc-600 text-[14px] max-w-2xl mb-8">
        Direct AWS Marketplace subscriptions expiring within 90 days. Dispatch
        the agent to draft an Agreement-Based Offer through the recommended VAR
        before revenue slips.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mb-6 border border-zinc-200">
        {[
          { label: "Expiring subs", value: subs.length, icon: Clock },
          {
            label: "At-risk ARR",
            value: fmtUSD(subs.reduce((s, x) => s + x.current_arr_usd, 0)),
            icon: Zap,
            accent: "text-orange-600",
          },
          {
            label: "Nearest expiry",
            value: subs.length ? `${Math.min(...subs.map((s) => s.days_until_expiry))} days` : "—",
            icon: Clock,
          },
        ].map((k, i, a) => {
          const Icon = k.icon;
          return (
            <div
              key={k.label}
              className={`p-6 ${i < a.length - 1 ? "md:border-r border-zinc-200" : ""}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="tx-overline">{k.label}</div>
                <Icon className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
              </div>
              <div className={`font-heading font-black text-3xl ${k.accent || "text-zinc-900"}`}>
                {k.value}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border border-zinc-200 bg-zinc-50 divide-y divide-zinc-200">
        {subs.map((s) => (
          <div
            key={s.subscription_id}
            data-testid={`sub-${s.subscription_id}`}
            className="p-6 flex items-start justify-between gap-6"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="font-mono text-[11px] text-zinc-500">{s.subscription_id}</span>
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${urgencyColor(s.days_until_expiry)}`}
                >
                  <span className="w-1 h-1 rounded-full bg-current" />
                  {s.days_until_expiry} days
                </span>
              </div>
              <div className="font-heading text-xl text-zinc-900 mb-1">{s.buyer_account}</div>
              <div className="text-[12px] text-zinc-600 font-mono">
                {s.buyer_aws_account_id} · {s.product_name}
              </div>
              <div className="text-[12px] text-zinc-500 mt-2">
                Recommended VAR:{" "}
                <span className="text-zinc-800 font-medium">{s.recommended_reseller}</span>
                {s.opportunity_id && (
                  <>
                    {" "}· CRM: <span className="font-mono">{s.opportunity_id}</span>
                  </>
                )}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono text-[11px] text-zinc-500 mb-1">Current ARR</div>
              <div className="font-heading font-black text-2xl">{fmtUSD(s.current_arr_usd)}</div>
              <Button
                onClick={() => dispatchAutopilot(s)}
                disabled={loading === s.subscription_id}
                data-testid={`autopilot-${s.subscription_id}`}
                className="mt-3 bg-orange-600 hover:bg-orange-500 disabled:opacity-40 text-white rounded-sm gap-2 h-9 px-3 text-[12px]"
              >
                <Zap className="w-3.5 h-3.5" />
                {loading === s.subscription_id ? "Dispatching…" : "Dispatch autopilot"}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
